// @flow

import React, { Component } from 'react';
import i18n from 'app/common/lib/i18n';
import PropTypes from 'prop-types';
import trackRegisterAction from 'app/common/lib/util/trackRegisterAction';
import type { PinType } from 'app/common/react/components/growth/unauth/lib/PropTypes';
import { getPinMeasurements } from 'app/common/react/components/growth/unauth/pin/pinUtils';
import { Icon } from 'gestalt';

const pinMeasurements = getPinMeasurements();

type Props = {
  handleClickSeeMore?: (expandedVASE: boolean) => {},
  pin: PinType,
  vaseClickHandler: (pin: PinType) => void,
  vaseDescriptionsMarkup: any,
  pinGridOptGroup?: string,
};

const styles = {
  seeMoreLinkSemantic: {
    backgroundColor: 'transparent',
    color: '#333',
    fontSize: '12px',
    fontWeight: 'bold',
    MozOsxFontSmoothing: 'grayscale',
    outline: 'none',
    WebkitFontSmoothing: 'antialiased',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  seeMoreLink: {
    backgroundColor: 'transparent',
    border: 0,
    color: '#333',
    fontSize: '12px',
    fontWeight: 'bold',
    display: 'flex',
    justifyContent: 'flex-end',
    height: pinMeasurements.seeMoreButtonHeight + 'px',
    margin: '0 0 ' + pinMeasurements.seeMoreButtonBottomMargin + 'px 0',
    MozOsxFontSmoothing: 'grayscale',
    outline: 'none',
    padding: 0,
    WebkitFontSmoothing: 'antialiased',
  },
  seeMoreLinkRemoveInlineStyle: {
    border: 0,
    display: 'flex',
    height: pinMeasurements.seeMoreButtonHeight + 'px',
    margin: '0 0 ' + pinMeasurements.seeMoreButtonBottomMargin + 'px 0',
    padding: 0,
  },
};

export default class PinDescriptionSeeMore extends Component<Props, void> {
  handleSeeMoreToggle() {
    const { pin, handleClickSeeMore, vaseClickHandler } = this.props;
    if (handleClickSeeMore) {
      handleClickSeeMore(!pin.expandedVASE);
    }
    trackRegisterAction('unauth.pin_card.see_more.click');
    vaseClickHandler(pin);
  }

  handleSeeMoreClick(e: SyntheticEvent<>) {
    e.stopPropagation();
    this.handleSeeMoreToggle();
  }

  render() {
    const { vaseDescriptionsMarkup, pin, pinGridOptGroup = '' } = this.props;

    const inCleanupPinGridStructureGroup = pinGridOptGroup.startsWith('enabled_structure_outline');
    const inSemanticGroup = pinGridOptGroup.startsWith('enabled_clean');

    const text = pin.expandedVASE ? i18n._('See less') : i18n._('See more');
    const icon = pin.expandedVASE ? 'arrow-up' : 'arrow-down';

    if (pin.expandedVASE) {
      trackRegisterAction('unauth.pin_card.see_more.expanded');
    }

    // If in hidden description experiment, add more padding above "See more"
    let seeMoreLinkStyles = {};
    if (!inSemanticGroup) {
      seeMoreLinkStyles = inSemanticGroup ? styles.seeMoreLinkSemantic : styles.seeMoreLink;
    }

    /* eslint-disable jsx-a11y/no-noninteractive-element-to-interactive-role */ return inSemanticGroup ? (
      <details
        role="button"
        id={`relatedPin${pin.id}`}
        data-test-id="seemoretoggle"
        className={inSemanticGroup ? 'PinDescriptionSeeMore_details' : ''}
        onToggle={(e: SyntheticEvent<>) => this.handleSeeMoreToggle()}
        onClick={(e: SyntheticEvent<>) => e.stopPropagation()}
        onKeyDown={(e: SyntheticEvent<>) => this.handleSeeMoreClick(e)}
      >
        <summary
          className={'PinDescriptionSeeMore_summary underlineLink'}
          style={seeMoreLinkStyles}
        >
          {inSemanticGroup ? (
            <Icon icon={icon} accessibilityLabel="expand" color="gray" size="12" inline />
          ) : (
            text
          )}
        </summary>
        {vaseDescriptionsMarkup}
      </details> /* eslint-enable jsx-a11y/no-noninteractive-element-to-interactive-role */
    ) : (
      <button
        data-test-id="seemoretoggle"
        className={'PinDescriptionSeeMore_summary underlineLink'}
        onClick={(e: SyntheticEvent<>) => this.handleSeeMoreClick(e)}
        style={seeMoreLinkStyles}
      >
        {inCleanupPinGridStructureGroup ? null : text}
        <span style={{ marginLeft: '4px' }}>
          <Icon icon={icon} accessibilityLabel="expand" color="gray" size="10" inline />
        </span>
      </button>
    );
  }
}

PinDescriptionSeeMore.propTypes = {
  pin: PropTypes.shape({
    expandedVASE: PropTypes.bool,
    id: PropTypes.string,
    description: PropTypes.string.isRequired,
  }),
  handleClickSeeMore: PropTypes.func,
  vaseClickHandler: PropTypes.func.isRequired,
  vaseDescriptionsMarkup: PropTypes.shape({}),
  pinGridOptGroup: PropTypes.string,
};
